import { Fragment, useMemo, useState } from "react";
import { ChevronDown, Eye, Pin, Settings2, X } from "lucide-react";
import { toast } from "sonner";

import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import { cn } from "@/lib/utils";
import useAuthorized from "hooks/useAuthorized";
import UserService from "services/User";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { isActionsColumn } from "components/BaseTableV3/table-helpers";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "components/ui/alert-dialog";
import { Button, buttonVariants } from "components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "components/ui/drawer";
import { Input } from "components/ui/input";
import { SaveButton } from "components/ui/save-button";
import { Separator } from "components/ui/separator";
import { Switch } from "components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

// A custom field only becomes a real column once it's visible (see
// addCustomFieldsColumns), so the picker is built from the tenant's field
// definitions rather than from the columns the table happens to have — that way
// every custom field is listed, selected or not.
const buildSettingsFields = ({
  allColumns = [],
  customFields = [],
  enableCustomFieldsSettings = false,
}) => {
  const fields = allColumns
    .filter(
      c =>
        !c?.meta?.isCustomField &&
        (typeof c.header === "string" || isActionsColumn(c))
    )
    .map(c => ({
      id: c.id,
      accessorKey: c.accessorKey,
      label: isActionsColumn(c) ? "Actions" : c.header,
      // The actions column renders an empty header and the table always draws
      // it, so it can be frozen or unfrozen but never hidden.
      freezeOnly: isActionsColumn(c),
    }));

  if (!enableCustomFieldsSettings) return fields;

  const seen = new Set(fields.map(f => f.id));
  const addCustomField = (id, label, group) => {
    if (seen.has(id)) return;
    seen.add(id);
    fields.push({ id, label, group, isCustomField: true });
  };

  customFields.forEach(cf =>
    cf.fields.forEach(field =>
      addCustomField(
        `${cf.accessorPrefix}.${field}`,
        field,
        cf.headerPrefix || "Custom"
      )
    )
  );

  // A field dropped from the tenant's config can still be a live column, so it
  // stays listed — otherwise there'd be no way to turn it off.
  allColumns
    .filter(c => c?.meta?.isCustomField && typeof c.header === "string")
    .forEach(c => {
      const [prefix, ...rest] = c.header.split(".");
      addCustomField(
        c.id,
        rest.length ? rest.join(".") : c.header,
        rest.length ? prefix : "Custom"
      );
    });

  return fields;
};

// Mirrors the placement the modal settings used: a newly shown field lands at
// the front so it's visible without scrolling right, while a custom field goes
// just before the trailing Actions column.
const withColumnShown = (columnOrder, field) => {
  const next = Array.from(columnOrder);
  const actionsIndex = next.findIndex(
    c => typeof c === "string" && c.toLowerCase() === "actions"
  );

  if (field.isCustomField && actionsIndex !== -1) {
    next.splice(actionsIndex, 0, field.id);
    return next;
  }

  return [field.id, ...next];
};

function SettingsSection({
  icon: Icon,
  title,
  selectedCount,
  total,
  collapsed,
  onToggle,
  children,
}) {
  return (
    <div className="tw:flex tw:flex-col">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:px-2 tw:py-1.5 tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase tw:transition-colors tw:hover:bg-muted/60"
      >
        <ChevronDown
          className={cn(
            "tw:size-3.5 tw:transition-transform",
            collapsed && "tw:-rotate-90"
          )}
        />
        <Icon className="tw:size-3.5" />
        <span>{title}</span>
        <span className="tw:ml-auto tw:tabular-nums">
          {selectedCount}/{total}
        </span>
      </button>
      {collapsed ? null : (
        <div className="tw:flex tw:flex-col tw:py-1">{children}</div>
      )}
    </div>
  );
}

function FieldToggle({ id, label, checked, onCheckedChange }) {
  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:rounded-md tw:px-2 tw:py-1.5 tw:transition-colors tw:hover:bg-muted/60">
      <label
        htmlFor={id}
        title={label}
        className="tw:min-w-0 tw:cursor-pointer tw:truncate tw:text-sm tw:text-foreground"
      >
        {label}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

// Custom fields are grouped behind a labelled divider rather than a per-row
// badge, which would compete with long field names for the row's width.
function FieldToggleList({ fields, idPrefix, isChecked, onToggle }) {
  const groups = fields.reduce((acc, field) => {
    const group = field.group ?? "";
    const bucket = acc.get(group) ?? [];
    return acc.set(group, [...bucket, field]);
  }, new Map());

  // The table's own columns always come first: a divider at the top of the list
  // reads as though every field below it belongs to that group.
  const columnFields = groups.get("") ?? [];
  const customFieldGroups = Array.from(groups).filter(([group]) => group);
  const sections = columnFields.length
    ? [["", columnFields], ...customFieldGroups]
    : customFieldGroups;

  return sections.map(([group, groupFields]) => (
    <Fragment key={`${idPrefix}-${group}`}>
      {group ? (
        <div className="tw:flex tw:items-center tw:gap-2 tw:px-2 tw:pt-3 tw:pb-1">
          <span className="tw:text-[11px] tw:font-medium tw:tracking-wide tw:text-muted-foreground tw:uppercase">
            {group}
          </span>
          <Separator className="tw:flex-1" />
        </div>
      ) : null}
      {groupFields.map(field => (
        <FieldToggle
          key={`${idPrefix}-${field.id}`}
          id={`${idPrefix}-${field.id}`}
          label={field.label}
          checked={isChecked(field)}
          onCheckedChange={checked => onToggle(field, checked)}
        />
      ))}
    </Fragment>
  ));
}

export function BaseTableV3SettingsDrawer({
  tableName,
  allColumns = [],
  customFields = [],
  visibleColumns: savedVisibleColumns = [],
  freezedColumns: savedFreezedColumns = [],
  enableCustomFieldsSettings = false,
  updateTableSettings,
  isLoading = false,
}) {
  const [open, setOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [freezedColumns, setFreezedColumns] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [collapsed, setCollapsed] = useState({ show: false, freeze: false });

  const canEditSettings = useAuthorized([ACL_RELATIONSHIPS.userSettings.edit]);

  const fields = useMemo(
    () =>
      buildSettingsFields({
        allColumns,
        customFields,
        enableCustomFieldsSettings,
      }),
    [allColumns, customFields, enableCustomFieldsSettings]
  );

  const hideableFields = useMemo(
    () => fields.filter(field => !field.freezeOnly),
    [fields]
  );

  const isShown = field =>
    visibleColumns.includes(field.id) ||
    (field.accessorKey != null && visibleColumns.includes(field.accessorKey));

  // Freezing only means something for a field that's on the table, and the list
  // follows the current column order so it reads like the table itself. Fields
  // the table always draws sort last, matching where they sit on screen.
  const freezableFields = useMemo(() => {
    const position = new Map(visibleColumns.map((id, index) => [id, index]));
    const isFreezable = field =>
      field.freezeOnly ||
      position.has(field.id) ||
      (field.accessorKey != null && position.has(field.accessorKey));

    return fields
      .filter(field => field.id !== SELECT_COLUMN_ID && isFreezable(field))
      .sort(
        (a, b) =>
          (position.get(a.id) ?? position.get(a.accessorKey) ?? Infinity) -
          (position.get(b.id) ?? position.get(b.accessorKey) ?? Infinity)
      );
  }, [fields, visibleColumns]);

  const query = search.trim().toLowerCase();
  const matchesSearch = field => field.label.toLowerCase().includes(query);
  const shownSectionFields = hideableFields.filter(matchesSearch);
  const freezeSectionFields = freezableFields.filter(matchesSearch);

  const handleOpenChange = nextOpen => {
    if (nextOpen) {
      setVisibleColumns(savedVisibleColumns);
      setFreezedColumns(savedFreezedColumns);
      setSearch("");
    }
    setOpen(nextOpen);
  };

  const toggleShown = (field, checked) => {
    if (checked) {
      setVisibleColumns(current => withColumnShown(current, field));
      return;
    }
    setVisibleColumns(current => current.filter(c => c !== field.id));
    setFreezedColumns(current => current.filter(c => c !== field.id));
  };

  const toggleFrozen = (field, checked) =>
    setFreezedColumns(current =>
      checked ? [...current, field.id] : current.filter(c => c !== field.id)
    );

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTableSettings({ visibleColumns, freezedColumns });
      setOpen(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Column widths, order and pinning are spread across table state and caches,
  // so a reload is the cheapest way to land the table on the defaults.
  const resetTableSettings = async () => {
    setResetting(true);
    try {
      await UserService.resetTable(tableName);
      await queryClient.invalidateQueries({ queryKey: ["table-settings"] });
      window.location.reload();
    } catch (error) {
      toast.error(error.message);
      setResetting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} swipeDirection="right">
      <Tooltip>
        <TooltipTrigger
          render={
            <DrawerTrigger
              aria-label="Table settings"
              disabled={isLoading}
              render={
                <Button variant="outline" size="icon">
                  <Settings2 />
                </Button>
              }
            />
          }
        />
        <TooltipContent>Table settings</TooltipContent>
      </Tooltip>
      <DrawerContent className="tw:sm:[--drawer-content-width:26rem]">
        <DrawerHeader className="tw:gap-3 tw:pb-3 tw:text-left">
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:pb-2">
            <DrawerTitle>Table settings</DrawerTitle>
            <DrawerClose
              className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
              aria-label="Close"
            >
              <X />
            </DrawerClose>
          </div>
          <Input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search fields"
            aria-label="Search fields"
          />
        </DrawerHeader>
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-1 tw:overflow-y-auto tw:px-4 tw:py-2">
          <SettingsSection
            icon={Eye}
            title="Show"
            selectedCount={hideableFields.filter(isShown).length}
            total={hideableFields.length}
            collapsed={collapsed.show}
            onToggle={() => setCollapsed(c => ({ ...c, show: !c.show }))}
          >
            {shownSectionFields.length ? (
              <FieldToggleList
                fields={shownSectionFields}
                idPrefix="show"
                isChecked={isShown}
                onToggle={toggleShown}
              />
            ) : (
              <p className="tw:px-2 tw:py-1.5 tw:text-sm tw:text-muted-foreground">
                No fields match “{search}”
              </p>
            )}
          </SettingsSection>
          <Separator className="tw:my-1" />
          <SettingsSection
            icon={Pin}
            title="Freeze"
            selectedCount={
              freezableFields.filter(field => freezedColumns.includes(field.id))
                .length
            }
            total={freezableFields.length}
            collapsed={collapsed.freeze}
            onToggle={() => setCollapsed(c => ({ ...c, freeze: !c.freeze }))}
          >
            {freezeSectionFields.length ? (
              <FieldToggleList
                fields={freezeSectionFields}
                idPrefix="freeze"
                isChecked={field => freezedColumns.includes(field.id)}
                onToggle={toggleFrozen}
              />
            ) : (
              <p className="tw:px-2 tw:py-1.5 tw:text-sm tw:text-muted-foreground">
                {query
                  ? `No fields match “${search}”`
                  : "Show a field to make it freezable"}
              </p>
            )}
          </SettingsSection>
        </div>
        <DrawerFooter className="tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:border-t tw:border-border tw:pt-4">
          {canEditSettings ? (
            <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
              <AlertDialogTrigger
                className={buttonVariants({
                  variant: "destructive",
                  size: "sm",
                })}
              >
                Reset
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset table settings?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will set this table&apos;s settings back to their
                    original state.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={resetting}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={resetTableSettings}
                    disabled={resetting}
                  >
                    {resetting ? "Resetting…" : "Reset"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <span />
          )}
          <div className="tw:flex tw:gap-2">
            <DrawerClose
              className={buttonVariants({ variant: "outline", size: "sm" })}
            >
              Cancel
            </DrawerClose>
            <SaveButton size="sm" onClick={handleSave} saving={saving} />
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
